import React, { useState, useEffect, useRef } from 'react';
import SwipeRail from './components/SwipeRail';
import {
  doc,
  addDoc,
  collection,
  query,
  onSnapshot,
  orderBy,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';

const LettersToHeaven = ({ 
  memorial, 
  memorialId, 
  auth, 
  db, 
  getUserDisplayName, 
  handleLoginRedirect,
  capitalizeWords 
}) => {
  // MOVE ALL HOOKS TO THE TOP - BEFORE ANY CONDITIONAL LOGIC
  // Local state for letters functionality
  const [letters, setLetters] = useState([]);
  const [letterText, setLetterText] = useState('');
  const [submittingLetter, setSubmittingLetter] = useState(false);
  const [showMyLetters, setShowMyLetters] = useState(false);
  const [letterIsPrivate, setLetterIsPrivate] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showLetterWriter, setShowLetterWriter] = useState(false);

  // Subscribe to letters from Firebase - MOVED TO TOP
  useEffect(() => {
    if (!memorialId || !memorial || !db) return;
    
    const q = query(
      collection(db, 'memorials', memorialId, 'letters'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      const lettersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLetters(lettersData);
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial, db]);

  // NOW WE CAN DO EARLY RETURNS AFTER ALL HOOKS
  // Return early if required props are missing
  if (!memorial || !memorialId || !auth || !db) {
    return null;
  }

  // Get Heaven's postal code
  const getHeavenPostalCode = () => "H34V3N";

 // Handle letter submission - now accepts text and privacy parameters
const handleSubmitLetter = async (textToSubmit, isPrivate) => {
  if (!auth.currentUser) {
    alert('Please log in to write a letter.');
    if (handleLoginRedirect) handleLoginRedirect();
    return;
  }
  
  if (!textToSubmit || !textToSubmit.trim()) {
    alert('Please write a letter before submitting.');
    return;
  }

  setSubmittingLetter(true);
  try {
    await addDoc(collection(db, 'memorials', memorialId, 'letters'), {
      userName: getUserDisplayName(auth.currentUser),
      userId: auth.currentUser.uid,
      authorEmail: auth.currentUser.email,
      text: textToSubmit.trim(),
      timestamp: new Date(),
      isPrivate: isPrivate !== undefined ? isPrivate : letterIsPrivate, // fallback to state if not provided
    });
    setLetterText('');
    setLetterIsPrivate(true);
    setShowLetterWriter(false);
    
    const privacyText = isPrivate ? 'sealed with wax' : 'sent and delivered';
    alert(`Your letter has been ${privacyText}! ${isPrivate ? 'Only you can break the wax seal and read it.' : 'Others can now read this letter.'}`);
    
  } catch (error) {
    console.error('Error submitting letter:', error);
    alert('Error submitting letter. Please try again.');
  } finally {
    setSubmittingLetter(false);
  }
}; 

  // Handle letter deletion
  const handleDeleteLetter = async (letterId) => {
    if (!auth.currentUser) return;

    if (!window.confirm('Are you sure you want to delete this letter forever? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'memorials', memorialId, 'letters', letterId));
    } catch (error) {
      console.error('Error deleting letter:', error);
      alert('Error deleting letter. Please try again.');
    }
  };

  // Handle letter privacy toggle
  const handleToggleLetterPrivacy = async (letterId, currentPrivacy) => {
    try {
      await updateDoc(doc(db, 'memorials', memorialId, 'letters', letterId), {
        isPrivate: !currentPrivacy,
        updatedAt: new Date()
      });
      
      const newStatus = !currentPrivacy ? 'sealed (private)' : 'unsealed (public)';
      alert(`Letter is now ${newStatus}. ${!currentPrivacy ? 'Only you can read it.' : 'Others can now read it.'}`);
      
    } catch (error) {
      console.error('Error updating letter privacy:', error);
      alert('Error updating letter privacy. Please try again.');
    }
  };

  // Filter letters based on privacy and ownership
  const getVisibleLetters = () => {
    if (showMyLetters && auth.currentUser) {
      return letters.filter(letter => letter.userId === auth.currentUser.uid);
    }
    
    // Show all letters to all users, but they can only read their own private ones
    return letters;
  };

  // Mobile-Optimized Envelope Component
  const EnvelopeCard = ({ letter, onClick }) => {
    const isSealed = letter.isPrivate;
    const isOwnLetter = auth.currentUser && letter.userId === auth.currentUser.uid;
    const canOpenLetter = !isSealed || isOwnLetter;
    
    const handleClick = () => {
      if (!canOpenLetter) {
        alert('This letter is sealed with wax. Only the author can break the seal and read it.');
        return;
      }
      onClick(letter);
    };
    
    return (
      <div 
        className={`relative ${canOpenLetter ? 'cursor-pointer' : 'cursor-not-allowed'} group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:shadow-md`}
        onClick={handleClick}
      >
        {/* Envelope Shadow - Reduced for mobile */}
        <div className="absolute inset-0 bg-gray-400/20 translate-x-1 translate-y-1 md:translate-x-2 md:translate-y-2"></div>
        
        {/* Main Envelope - Mobile optimized */}
        <div className={`relative bg-gradient-to-br ${isSealed ? 'from-[#eef0fa] to-[#dde3f4] border-[#a9b2d8]' : 'from-[#fdf8ec] to-[#f3e7c9] border-[#cbab6a]'} border rounded-lg p-4 sm:p-6 min-h-[180px] sm:min-h-[220px] bg-white ${!canOpenLetter ? 'opacity-75' : ''}`}>
          
          {/* Envelope Flap - Smaller on mobile */}
          <div className={`absolute -top-1 left-1/2 transform -translate-x-1/2 w-20 h-10 sm:w-28 sm:h-14 ${isSealed ? 'bg-[#e3e7f6] border-[#a9b2d8]' : 'bg-[#f5ead0] border-[#cbab6a]'} border border-b-0`}
               style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)' }}>
          </div>

          {/* Wax Seal for Private Letters - Larger touch target */}
          {isSealed && (
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold shadow-xl border-2 border-red-700 z-10">
              🔒
            </div>
          )}

          {/* Postal Stamp - Mobile responsive */}
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 w-14 h-16 sm:w-18 sm:h-22 bg-white border-2 border-gray-400 p-1 sm:p-2 transform rotate-3 shadow-lg">
            <div className="w-full h-full flex flex-col items-center justify-center text-white" style={{ background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)' }}>
              <div className="text-sm sm:text-xl mb-1" style={{ color: '#ffe2a8' }}>✦</div>
              <div className="text-[7px] sm:text-[9px] font-bold">MEMORY</div>
              <div className="text-[6px] sm:text-[8px]">$0.63</div>
            </div>
            {/* Postal Cancellation Marks */}
            <div className="absolute inset-0 border-2 border-black opacity-40 rounded-full transform -rotate-12"></div>
            <div className="absolute top-1 right-1 w-2 h-0.5 sm:w-4 sm:h-1 bg-black opacity-40"></div>
            <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-1 w-2 h-0.5 sm:w-3 sm:h-1 bg-black opacity-40 transform rotate-45"></div>
          </div>

          {/* Address Section - Better mobile typography */}
          <div className="mt-8 sm:mt-10 space-y-2 sm:space-y-3">
            <div className="text-xs sm:text-sm">
              <div className="font-bold text-gray-800 mb-1 text-xs sm:text-sm" style={{ fontFamily: 'Courier New, monospace' }}>
                TO: {memorial.firstName.toUpperCase()} {memorial.lastName.toUpperCase()}
              </div>
              <div className="text-gray-600 text-[10px] sm:text-xs" style={{ fontFamily: 'Courier New, monospace' }}>
                c/o Heaven's Gates
              </div>
              <div className="text-gray-600 text-[10px] sm:text-xs" style={{ fontFamily: 'Courier New, monospace' }}>
                Cloud District, {getHeavenPostalCode()}
              </div>
            </div>

            <div className="text-xs sm:text-sm pt-3 sm:pt-4 border-t border-gray-300/50">
              <div className="text-gray-700 font-medium text-xs sm:text-sm" style={{ fontFamily: 'Courier New, monospace' }}>
                FROM: {letter.userName}
              </div>
              <div className="text-gray-500 text-[10px] sm:text-xs mt-1" style={{ fontFamily: 'Courier New, monospace' }}>
                POSTED: {letter.timestamp?.toDate ? 
                  letter.timestamp.toDate().toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }).toUpperCase() :
                  new Date(letter.timestamp).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }).toUpperCase()
                }
              </div>
            </div>
          </div>

          {/* Privacy Status Badge - Better mobile sizing */}
          <div className="absolute bottom-2 left-2">
            <div className={`px-2 py-1 text-[10px] sm:text-xs font-bold rounded shadow-sm ${
              isSealed 
                ? 'bg-red-100 text-red-700 border border-red-300' 
                : 'bg-green-100 text-green-700 border border-green-300'
            }`}>
              {isSealed ? 'SEALED' : 'DELIVERED'}
            </div>
          </div>

          {/* Postal Processing Mark */}
          <div className="absolute bottom-2 right-2 opacity-60">
            <div className="text-[10px] sm:text-xs text-gray-500 transform -rotate-12 font-bold" style={{ fontFamily: 'Courier New, monospace' }}>
              PROCESSED ✔
            </div>
          </div>

          {/* Letter Controls for Own Letters - Larger touch targets for mobile */}
          {isOwnLetter && (
            <div className="absolute top-1 left-1 sm:top-2 sm:left-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
              <div className="flex gap-1 sm:gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleLetterPrivacy(letter.id, letter.isPrivate);
                  }}
                  className="w-8 h-8 sm:w-6 sm:h-6 bg-white/95 hover:bg-white rounded-full flex items-center justify-center text-xs shadow-lg transition-colors border border-gray-300 touch-manipulation"
                  title={letter.isPrivate ? "Unseal letter (make public)" : "Seal letter (make private)"}
                >
                  {letter.isPrivate ? '🔓' : '🔒'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLetter(letter.id);
                  }}
                  className="w-8 h-8 sm:w-6 sm:h-6 bg-white/95 hover:bg-red-100 rounded-full flex items-center justify-center text-xs shadow-lg transition-colors border border-gray-300 touch-manipulation"
                  title="Delete letter forever"
                >
                  🗑️
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Mobile-Optimized Letter Writer Component - FIXED POSITIONING
  const LetterWriter = ({ onClose }) => {
    const textareaRef = useRef(null);
    const [localLetterText, setLocalLetterText] = useState(letterText);
    const [localIsPrivate, setLocalIsPrivate] = useState(letterIsPrivate);
    const [charCount, _setCharCount] = useState(0);

  const handleSubmit = () => {
    if (!localLetterText.trim()) {
      alert('Please write a letter before submitting.');
      return;
    }
    setLetterText(localLetterText);
    setLetterIsPrivate(localIsPrivate);
    handleSubmitLetter(localLetterText.trim(), localIsPrivate);
  };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-2 sm:p-4 pt-16 sm:pt-24" onClick={onClose}>
        <div className="bg-white w-full max-w-6xl mx-2 sm:mx-4 shadow-2xl relative max-h-[90vh] sm:max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          
          {/* Writing Desk Header - Mobile optimized */}
          <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
            <div className="flex items-center justify-between flex-col sm:flex-row gap-4 sm:gap-0">
              <div className="text-center sm:text-left">
                <h3 className="text-2xl sm:text-3xl font-light text-gray-800" style={{ fontFamily: 'Garamond, serif' }}>
                  Letters to Heaven
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Express your heart in your own words to {capitalizeWords(memorial.firstName)}
                </p>
              </div>
              <div className="text-center sm:text-right text-xs sm:text-sm text-gray-500" style={{ fontFamily: 'Courier New, monospace' }}>
                TODAY:<br/>
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }).toUpperCase()}
              </div>
            </div>
          </div>

          {/* Writing Paper with Lines - Mobile optimized */}
          <div className="p-4 sm:p-10 bg-gradient-to-br from-white via-[#fdf8ec] to-[#faf6ec] relative" 
               style={{ 
                 backgroundImage: `repeating-linear-gradient(transparent, transparent 24px, #d1d5db 24px, #d1d5db 25px)`,
                 lineHeight: '25px'
               }}>
            
            {/* Red Margin Line - Responsive positioning */}
            <div className="absolute left-8 sm:left-16 top-0 bottom-0 w-px bg-red-400 opacity-60"></div>
            
            {/* Letter Header - Mobile optimized */}
            <div>
              <div className="text-xs sm:text-sm text-gray-700 mb-4 sm:mb-6" style={{ fontFamily: 'Courier New, monospace' }}>
                <div className="font-bold" style={{ transform: 'translate(30px, -8px)' }}>TO:</div>
                <div className="ml-4 sm:ml-8" style={{ transform: 'translate(20px, -4px)'}}>{capitalizeWords(memorial.firstName)} {capitalizeWords(memorial.lastName)}</div>
                <div className="ml-4 sm:ml-8" style={{ transform: 'translate(20px, 0px)'}}>Heaven's Beautiful Gardens</div>
                <div className="ml-4 sm:ml-8" style={{ transform: 'translate(20px, 4px)'}}>Among the Stars, {getHeavenPostalCode()}</div>
              </div>
              
              <div className="text-lg sm:text-xl text-gray-800 mb-2 ml-2 sm:ml-4" style={{ fontFamily: '"Dancing Script", cursive', fontSize: '18px', transform: 'translate(34px, 4px)' }}>
                Dear {capitalizeWords(memorial.firstName)},
              </div>
            </div>

            {/* Enhanced Letter Content Textarea - Mobile optimized */}
            <div style={{ transform: 'translate(20px, -4px)'}}>
              <textarea
                ref={textareaRef}
                value={localLetterText}
                onChange={(e) => {
                  setLocalLetterText(e.target.value);
                }}
                placeholder="Type your letter here..."
                className="w-full min-h-[80px] sm:min-h-[100px] bg-transparent border-none outline-none resize-none text-gray-800 placeholder-gray-400 text-base sm:text-lg pl-6 sm:pl-8"
                style={{ 
                  fontFamily: '"Dancing Script", cursive',
                  fontSize: '16px',
                  lineHeight: '25px',
                  direction: 'ltr'
                }}
                maxLength={2000}
                dir="ltr"
              />
              <div className="flex justify-between items-end mt-3" style={{ paddingTop: '50px' }}>
                <span className="text-xs text-gray-500" style={{ fontFamily: 'Courier New, monospace' }}>
                  CHARACTERS: {localLetterText.length}/2000
                </span>
                <div className="text-right">
                  <div className="text-gray-700 mb-0 text-base sm:text-lg" style={{ fontFamily: '"Dancing Script", cursive' }}>
                    With all my love,
                  </div>
                  <div className="text-gray-800 font-semibold text-lg sm:text-xl" style={{ fontFamily: '"Dancing Script", cursive' }}>
                    {auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Your Name'}
                  </div>
                </div>
              </div>
            </div>
            {/* Paper decorative elements - Simplified for mobile */}
            <div className="absolute top-4 right-4 w-6 h-6 sm:w-8 sm:h-8 border-2 border-gray-300 opacity-20 transform rotate-45"></div>
            <div className="absolute bottom-8 left-12 sm:left-20 w-4 h-4 sm:w-6 sm:h-6 border border-gray-400 opacity-15 rounded-full"></div>
            
            {/* Hole punch marks - Responsive positioning */}
            <div className="absolute left-3 sm:left-6 top-16 w-2 h-2 bg-white rounded-full border border-gray-300 shadow-inner"></div>
            <div className="absolute left-3 sm:left-6 top-32 w-2 h-2 bg-white rounded-full border border-gray-300 shadow-inner"></div>
            <div className="absolute left-3 sm:left-6 top-48 w-2 h-2 bg-white rounded-full border border-gray-300 shadow-inner"></div>
          </div>

          {/* Letter Controls - Mobile optimized */}
          <div className="bg-[#faf9f7] p-4 sm:p-6 border-t border-[#eee9df]">
            
            {/* Privacy Selection - Better mobile layout */}
            <div className="mb-6 bg-white p-4 sm:p-5 rounded-xl border border-dashed border-[#d8c9a4]">
              <h4 className="font-medium text-gray-800 mb-3 text-center text-sm sm:text-base">Letter Delivery Options</h4>
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                
                <label className={`cursor-pointer p-3 sm:p-4 border-2 rounded transition-all touch-manipulation ${localIsPrivate ? 'border-[#a9b2d8] bg-[#eef0fa]' : 'border-[#eae6dd] bg-[#faf9f7]'}`}>
                  <input
                    type="radio"
                    name="privacy"
                    checked={localIsPrivate}
                    onChange={() => setLocalIsPrivate(true)}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl mb-2">🔒</div>
                    <div className="font-medium text-gray-800 mb-1 text-sm sm:text-base">Seal with Wax (Private)</div>
                    <div className="text-xs text-gray-600">Only you can break the seal and read this letter</div>
                  </div>
                </label>

                <label className={`cursor-pointer p-3 sm:p-4 border-2 rounded transition-all touch-manipulation ${!localIsPrivate ? 'border-[#cbab6a] bg-[#faf6ec]' : 'border-[#eae6dd] bg-[#faf9f7]'}`}>
                  <input
                    type="radio"
                    name="privacy"
                    checked={!localIsPrivate}
                    onChange={() => setLocalIsPrivate(false)}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl mb-2">📮</div>
                    <div className="font-medium text-gray-800 mb-1 text-sm sm:text-base">Send & Deliver (Public)</div>
                    <div className="text-xs text-gray-600">All memorial visitors can read this letter</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Submit Controls - Mobile optimized buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => {
                  setShowLetterWriter(false);
                  setLetterText('');
                  setLetterIsPrivate(true);
                }}
                className="w-full sm:w-auto px-6 py-4 bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors font-medium text-base touch-manipulation"
              >
                Discard Letter
              </button>
              <button
                onClick={handleSubmit}
                disabled={submittingLetter}
                className="w-full sm:w-auto sm-btn-primary touch-manipulation"
              >
                <span className="text-xl">{letterIsPrivate ? '🔒' : '📮'}</span>
                <span>
                  {submittingLetter ? 'Sending Letter...' : letterIsPrivate ? 'Seal & Save Letter' : 'Send & Share Letter'}
                </span>
              </button>
            </div>

            <div className="text-center mt-3">
              <span className="text-xs text-gray-500" style={{ fontFamily: 'Courier New, monospace' }}>
                CHARACTERS: {charCount}/2000
              </span>
            </div>
          </div>

          {/* Close Button - Larger touch target */}
          <button
            onClick={onClose}
            className="absolute top-2 left-2 w-12 h-12 sm:w-10 sm:h-10 bg-white/95 hover:bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors shadow-lg border border-gray-300 z-50 touch-manipulation text-lg sm:text-base"
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  // Mobile-Optimized Letter Reader Component - FIXED POSITIONING
  const LetterReader = ({ letter, onClose }) => {
    const isOwnLetter = auth.currentUser && letter.userId === auth.currentUser.uid;
    
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-2 sm:p-4 pt-20 sm:pt-24" onClick={onClose}>
        <div className="bg-white w-full max-w-5xl mx-2 sm:mx-4 shadow-2xl relative max-h-[90vh] sm:max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          
          {/* Letter Header - Mobile optimized */}
          <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
            <div className="text-center flex-1 mb-4">
              <h3 className="text-2xl sm:text-3xl font-light text-gray-800" style={{ fontFamily: 'Garamond, serif' }}>
                Letters to Heaven
              </h3>
              <div className="w-24 sm:w-32 h-px mx-auto mt-2" style={{ background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}></div>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-2" style={{ fontFamily: 'Courier New, monospace' }}>
                HEAVEN'S POSTAL SERVICE • EST. ETERNAL
              </p>
            </div>
            
            <div className="flex items-center justify-between flex-col sm:flex-row gap-2 sm:gap-0">
              <div>
                <span className="text-sm text-gray-600">From: {letter.userName}</span>
                <div className={`inline-block ml-3 px-2 py-1 text-xs font-bold rounded ${
                  letter.isPrivate 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {letter.isPrivate ? '🔒 SEALED MAIL' : '📮 DELIVERED MAIL'}
                </div>
              </div>
              <div className="text-center sm:text-right text-xs text-gray-500" style={{ fontFamily: 'Courier New, monospace' }}>
                POSTMARKED:<br/>
                {letter.timestamp?.toDate ? 
                  letter.timestamp.toDate().toLocaleDateString('en-US', { 
                    weekday: 'short',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }).toUpperCase() :
                  new Date(letter.timestamp).toLocaleDateString('en-US', { 
                    weekday: 'short',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }).toUpperCase()
                }
              </div>
            </div>
          </div>

          {/* Letter Content on Lined Paper - Mobile optimized */}
          <div className="p-4 sm:p-8 bg-gradient-to-br from-white via-[#fdf8ec] to-[#faf6ec] min-h-[200px] sm:min-h-[250px] relative" 
               style={{ 
                 backgroundImage: `repeating-linear-gradient(transparent, transparent 24px, #d1d5db 24px, #d1d5db 25px)`,
                 lineHeight: '25px'
               }}>
            
            {/* Red Margin Line */}
            <div className="absolute left-6 sm:left-12 top-0 bottom-0 w-px bg-red-300 opacity-60"></div>
            
            {/* Letter Date */}
            <div className="mb-6 sm:mb-8">
              <div className="text-right text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6" style={{ fontFamily: 'Courier New, monospace' }}>
                {letter.timestamp?.toDate ? 
                  letter.timestamp.toDate().toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }) :
                  new Date(letter.timestamp).toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })
                }
              </div>
              
              <div className="text-lg sm:text-xl text-gray-800 mb-4 sm:mb-6 ml-2 sm:ml-4" style={{ fontFamily: '"Dancing Script", cursive', fontSize: '18px', transform: 'translate(20px, -2px)'}}>
                Dear {capitalizeWords(memorial.firstName)},
              </div>
            </div>

            {/* Letter Body - Mobile optimized typography */}
            <div className="mb-8 sm:mb-12 ml-4 sm:ml-8" style={{transform: 'translate(2px, -10px)'}}>
              <div className="text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg" 
                   style={{ 
                     fontFamily: '"Dancing Script", cursive',
                     fontSize: '16px',
                     lineHeight: '25px'
                   }}>
                {letter.text}
              </div>
            </div>

            {/* Letter Signature - Mobile optimized */}
            <div className="mt-20 sm:mt-16 text-right ml-4 sm:ml-8">
              <div className="text-gray-700 mb-3 text-base sm:text-lg" style={{ fontFamily: '"Dancing Script", cursive', transform: 'translate(-10px, 24px)'}}>
                With all my love,
              </div>
              <div className="text-gray-800 font-semibold text-lg sm:text-1xl" style={{ fontFamily: '"Dancing Script", cursive', transform: 'translate(-20px, 10px)' }}>
                {letter.userName}
              </div>
              {letter.isEdited && (
                <div className="text-xs text-gray-400 mt-2 italic" style={{ fontFamily: 'Courier New, monospace' }}>
                  (LETTER REVISED {letter.editedAt?.toDate ? 
                    letter.editedAt.toDate().toLocaleDateString() : 
                    'RECENTLY'})
                </div>
              )}
            </div>

            {/* Paper Effects and Decorations - Simplified for mobile */}
            <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-yellow-100/50"></div>
            <div className="absolute bottom-0 left-0 w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100/30"></div>
            <div className="absolute top-1/4 right-4 sm:right-8 w-2 h-2 bg-yellow-200/70 rounded-full"></div>
            
            {/* Hole punch marks */}
            <div className="absolute left-2 sm:left-4 top-16 w-2 h-2 bg-white rounded-full border border-gray-300 shadow-inner"></div>
            <div className="absolute left-2 sm:left-4 top-32 w-2 h-2 bg-white rounded-full border border-gray-300 shadow-inner"></div>
            <div className="absolute left-2 sm:left-4 top-48 w-2 h-2 bg-white rounded-full border border-gray-300 shadow-inner"></div>
          </div>

          {/* Close Button - Larger touch target */}
          <button
            onClick={onClose}
            className="absolute top-2 left-2 w-12 h-12 sm:w-10 sm:h-10 bg-white/95 hover:bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors shadow-lg border border-gray-300 z-50 touch-manipulation text-lg sm:text-base"
          >
            ✕
          </button>

          {/* Letter Actions - Mobile optimized */}
          {isOwnLetter && (
            <div className="bg-gray-50 p-4 border-t border-gray-200">
              <div className="flex justify-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleLetterPrivacy(letter.id, letter.isPrivate);
                    onClose();
                  }}
                  className="w-full sm:w-auto px-6 py-3 rounded-full bg-white text-gray-700 border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all text-sm touch-manipulation"
                >
                  {letter.isPrivate ? '🔓 Unseal & Share' : '🔒 Seal as Private'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const visibleLetters = getVisibleLetters();
  const userLetters = letters.filter(letter => 
    auth.currentUser && letter.userId === auth.currentUser.uid
  );
  const publicLetters = letters.filter(letter => !letter.isPrivate);

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      
      <div className="text-center mb-8 sm:mb-12">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          Heaven's Post
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          Letters to Heaven
        </h2>
        <div
          className="mx-auto mt-4 mb-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="text-gray-500 text-sm font-light max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
          Send a personal letter that can remain forever sealed with wax, or deliver it for all memorial visitors to read and cherish
        </p>
      </div>

      {auth.currentUser ? (
        <div className="max-w-6xl mx-auto">
          
          {/* Post Office Controls - Mobile optimized */}
          <div className="bg-[#faf9f7] p-4 sm:p-6 rounded-2xl border border-[#eee9df] mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-4 sm:gap-6 flex-col sm:flex-row">
              <button
                onClick={() => setShowLetterWriter(true)}
                className="w-full sm:w-auto sm-btn-primary touch-manipulation"
              >
                <span className="text-2xl">✉️</span>
                <span className="font-medium">Write New Letter</span>
              </button>
              
              {userLetters.length > 0 && (
                <button
                  onClick={() => setShowMyLetters(!showMyLetters)}
                  className="w-full sm:w-auto sm-btn-primary touch-manipulation"
                >
                  <span className="text-2xl">{showMyLetters ? '📬' : '📪'}</span>
                  <span className="font-medium text-sm sm:text-base">
                    {showMyLetters ? `All Mail (${publicLetters.length})` : `My Mailbox (${userLetters.length})`}
                  </span>
                </button>
              )}
            </div>
            
            {/* Postal Service Header */}
            <div className="text-center mt-4 pt-4" style={{ borderTop: '1px solid #e2cf9f' }}>
              <p className="text-xs sm:text-sm font-bold text-gray-700" style={{ fontFamily: 'Courier New, monospace' }}>
                HEAVEN'S POSTAL SERVICE • DELIVERING LOVE SINCE ETERNITY
              </p>
            </div>
          </div>

          {/* Post Office Box / Mail Sorting Area - Mobile optimized */}
          <div className="bg-[#faf9f7] p-4 sm:p-8 rounded-2xl border border-[#eee9df] mb-6 sm:mb-8">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-medium text-gray-800 flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
                <span className="text-2xl sm:text-3xl">📮</span>
                <span className="break-words">MAILBOX: {memorial.firstName.toUpperCase()} {memorial.lastName.toUpperCase()}</span>
                <span className="text-2xl sm:text-3xl">📮</span>
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-3 break-words px-2" style={{ fontFamily: 'Courier New, monospace' }}>
                POSTAL ADDRESS: {memorial.firstName.toUpperCase()} {memorial.lastName.toUpperCase()} • C/O HEAVEN'S GATES • CLOUD DISTRICT • {getHeavenPostalCode()}
              </p>
              {visibleLetters.length > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-2 rounded-full border border-[#eae6dd] flex-wrap justify-center">
                  <span className="text-xs sm:text-sm font-medium" style={{ fontFamily: 'Courier New, monospace' }}>
                    📬 MAIL COUNT: {visibleLetters.length} LETTER{visibleLetters.length !== 1 ? 'S' : ''}
                  </span>
                  {showMyLetters && (
                    <span className="text-xs font-bold" style={{ color: '#b8964f' }}>• PRIVATE MAILBOX</span>
                  )}
                </div>
              )}
            </div>

            {/* Letters Grid - Mobile optimized */}
            {visibleLetters.length > 0 ? (
              <>
                {/* A shelf of mail: every envelope is a swipe away. Unlike the
                    flower and card rails (one keepsake per view), envelopes are
                    small index items — several stay in view so the mailbox still
                    *looks* full, which is half the feeling. */}
                <SwipeRail gap="gap-5 sm:gap-6" ariaLabel="Letters">
                  {visibleLetters.map((letter) => (
                    <div
                      key={letter.id}
                      style={{ scrollSnapAlign: 'start' }}
                      className="shrink-0 basis-[78%] sm:basis-[46%] lg:basis-[31%]"
                    >
                      <EnvelopeCard
                        letter={letter}
                        onClick={setSelectedLetter}
                      />
                    </div>
                  ))}
                </SwipeRail>

                {visibleLetters.length > 1 && (
                  <div className="text-center mb-2">
                    <span
                      className="inline-block text-[11px] uppercase text-gray-400"
                      style={{ letterSpacing: '0.22em', fontFamily: 'Courier New, monospace' }}
                    >
                      ✉ Swipe through all {visibleLetters.length} letters
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 sm:py-16">
                <div className="text-6xl sm:text-8xl mb-4 sm:mb-6">📭</div>
                <h4 className="text-xl sm:text-2xl font-light text-gray-800 mb-4">
                  {showMyLetters ? "Your mailbox is empty" : "Community mailbox is empty"}
                </h4>
                <p className="text-gray-500 text-base sm:text-lg mb-6 px-4">
                  {showMyLetters 
                    ? "You haven't written any letters yet"
                    : "No letters have been delivered to the public mailbox"
                  }
                </p>
                <button
                  onClick={() => setShowLetterWriter(true)}
                  className="w-full sm:w-auto sm-btn-primary touch-manipulation"
                >
                  ✏️ Write the First Letter
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Not Logged In State - Mobile optimized */
        <div className="text-center mb-8 bg-[#faf9f7] p-4 sm:p-8 max-w-4xl mx-auto rounded-2xl border border-dashed border-[#d8c9a4]">
          <div className="text-6xl sm:text-8xl mb-4 sm:mb-6">💌</div>
          <h3 className="text-2xl sm:text-3xl font-light text-gray-800 mb-4 px-4">
            Send a Letter to {capitalizeWords(memorial.firstName)}
          </h3>
          <p className="text-gray-600 mb-6 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto px-4">
            Write a personal letter that can be sealed forever with wax (private) or delivered for others to read and treasure
          </p>
          <div className="bg-blue-50/50 p-4 sm:p-6 mb-6 border border-blue-200 text-sm text-gray-800 max-w-xl mx-auto">
            <div className="space-y-2">
              <p>🔒 <strong>Sealed Letters:</strong> Private letters with wax seals - only you can read them</p>
              <p>📮 <strong>Delivered Letters:</strong> Public letters shared with all memorial visitors</p>
            </div>
          </div>
          <button
            onClick={handleLoginRedirect}
            className="w-full sm:w-auto sm-btn-primary touch-manipulation"
          >
            Log In to Write Letter
          </button>
        </div>
      )}

      {/* Letter Writer Modal */}
      {showLetterWriter && (
        <LetterWriter onClose={() => setShowLetterWriter(false)} />
      )}

      {/* Letter Reader Modal */}
      {selectedLetter && (
        <LetterReader 
          letter={selectedLetter} 
          onClose={() => setSelectedLetter(null)}
        />
      )}
    </div>
  );
};

export default LettersToHeaven;
